import type { FC } from 'react'

import { AssetFallbackIcon, StyledText } from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import type {
  EcomPolicyMandatoryProduct,
  PolicyCoreData,
  Product,
} from '@/components/ecom-policy/types'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { statusStyleByWhiteLabel } from '@/components/util/style-constant'

export const CartProduct: FC<{
  product: (Product | EcomPolicyMandatoryProduct) & {
    qty: number
  }
  price?: number
  newProductPrice?: number
  policy: PolicyCoreData
  onRemoveItem: (policyId: number, productId: number, force?: boolean) => void
}> = ({ product, price, newProductPrice, policy, onRemoveItem }) => {
  const { whiteLabel } = useAppContext()

  return (
    <div
      className={twJoin(
        'sm:shadow-basic flex max-w-full items-center justify-between gap-2 px-2 py-2 sm:gap-3 sm:rounded-lg sm:px-4',
        'max-sm:border-b max-sm:border-solid max-sm:border-transparent max-sm:border-b-gray-300/50 max-sm:last:border-b-transparent'
      )}
    >
      <div className="shadow-basic flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full">
        <ImageWithIconFallback
          src={product.imgUrl || ''}
          alt="product"
          className="max-h-[80%]"
          fallbackIcon={<AssetFallbackIcon assetType={product.assetType || 'OTHER'} size={1.5} />}
        />
      </div>

      <div className="flex w-3/5 shrink grow flex-col md:gap-0.5">
        <div className="text-sm text-gray-500 sm:text-base md:text-lg">
          {product?.alias || product?.name || <Translation id="Product Name" />}
        </div>
        {!product.isMandatory && (
          <div>
            {product.qty > 1 && (
              <StyledText size="large" className="max-md:!text-base max-sm:!text-sm">
                {product.qty}x{' '}
              </StyledText>
            )}
            <StyledText
              size="large"
              colorMode="black"
              className="max-md:!text-base max-sm:!text-sm"
            >
              {price === 0 ? (
                <Translation id="Fully covered" />
              ) : (
                <FormattedNumberWithCurrency
                  amount={price || 0}
                  strikeThrough={Boolean(newProductPrice)}
                />
              )}
            </StyledText>

            {newProductPrice && (
              <StyledText
                colorMode="red"
                size="large"
                className="max-md:!text-base max-sm:!text-sm"
              >
                <FormattedNumberWithCurrency
                  isTextBold
                  amount={newProductPrice || 0}
                  className="ml-1 sm:ml-2"
                />
              </StyledText>
            )}
          </div>
        )}
      </div>

      {!product.isMandatory && (
        <TrashCanOutlineIcon
          className={twJoin(
            'size-6 shrink-0 cursor-pointer sm:size-7',
            statusStyleByWhiteLabel[whiteLabel]?.error.text ||
              statusStyleByWhiteLabel.skytech!.error.text
          )}
          onClick={() => onRemoveItem(policy.id, Number(product.id))}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              onRemoveItem(policy.id, Number(product.id), false)
            }
          }}
          role="button"
          tabIndex={0}
        />
      )}
    </div>
  )
}
